/**
 * The highlight / text-colour swatch picker (`061-editor.md` §2). One component, used four
 * times — highlight and text colour, in both the fixed toolbar and the bubble menu.
 *
 * The swatch chips reference the same `--hl-*` / `--tc-*` tokens the marks render with
 * (`styles/theme.css`), so a swatch always matches the result of picking it, in either theme.
 * Selection is preserved the usual way: `onMouseDown` preventDefault on every button, and the
 * command runs through `editor.chain().focus()` in the parent, which restores the stored
 * ProseMirror selection even though the popover briefly took DOM focus.
 */
import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { PALETTE_COLORS, type PaletteColor } from '@/features/editor/marks';

interface ColorMenuProps {
  /** `highlight` fills the chip's background; `text` colours a letter. */
  variant: 'highlight' | 'text';
  /** Trigger tooltip / aria-label. */
  label: string;
  /** Trigger icon. */
  icon: React.ReactNode;
  /** The colour currently applied over the selection, or `null`. */
  active: PaletteColor | null;
  /** Pick a colour, or `null` to remove it. */
  onSelect: (color: PaletteColor | null) => void;
}

const triggerClass = cn(
  'flex size-6 items-center justify-center rounded-sm text-[var(--muted-foreground)]',
  'hover:bg-[var(--accent)] hover:text-[var(--foreground)]',
  'data-[state=open]:bg-[var(--accent)] data-[state=open]:text-[var(--foreground)]',
  '[&_svg]:size-3.5',
);

function swatchStyle(variant: 'highlight' | 'text', color: PaletteColor): React.CSSProperties {
  return variant === 'highlight'
    ? { backgroundColor: `var(--hl-${color})` }
    : { color: `var(--tc-${color})` };
}

export function ColorMenu({ variant, label, icon, active, onSelect }: ColorMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const pick = (color: PaletteColor | null) => {
    onSelect(color);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        aria-label={label}
        title={label}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        className={cn(triggerClass, active !== null && 'text-[var(--foreground)]')}
      >
        {icon}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-1.5"
        // Keep the editor selection visually; the trigger already prevented the blur.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        <div className="flex items-center gap-1">
          {PALETTE_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={t(`editor.color.${color}`)}
              aria-pressed={active === color}
              title={t(`editor.color.${color}`)}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                pick(color);
              }}
              className={cn(
                'flex size-6 items-center justify-center rounded-sm border border-[var(--border)]',
                'hover:ring-2 hover:ring-[var(--ring)]',
                active === color && 'ring-2 ring-[var(--ring)]',
                variant === 'text' && 'text-sm font-semibold',
              )}
              style={swatchStyle(variant, color)}
            >
              {variant === 'text' ? 'A' : null}
              {variant === 'highlight' && active === color ? (
                <Check className="size-3.5 text-[var(--foreground)]" />
              ) : null}
            </button>
          ))}

          <span aria-hidden className="mx-0.5 h-5 w-px bg-[var(--border)]" />

          <button
            type="button"
            aria-label={t('editor.color.remove')}
            title={t('editor.color.remove')}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => {
              pick(null);
            }}
            className={cn(
              'flex size-6 items-center justify-center rounded-sm text-[var(--muted-foreground)]',
              'hover:bg-[var(--accent)] hover:text-[var(--foreground)]',
              '[&_svg]:size-3.5',
            )}
          >
            <X />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
